import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

const ContentContext = createContext({ items: {}, locale: "es-AR", loading: true });
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Loads /api/content?lang=<resolved> on mount and on locale:change. Exposes
 * useContent(slug, fallback) for public-page copy that lives in the CMS.
 */
export function ContentProvider({ children }) {
  const { i18n } = useTranslation();
  const [state, setState] = useState({ items: {}, locale: i18n.language || "es-AR", loading: true });

  const fetchContent = useCallback(async (lng) => {
    try {
      const res = await fetch(`${API}/content?lang=${encodeURIComponent(lng)}`);
      const data = await res.json();
      setState({ items: data.items || {}, locale: data.locale || lng, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchContent(i18n.language || "es-AR");
    const onLocale = () => fetchContent(i18n.language || "es-AR");
    const onRefresh = () => fetchContent(i18n.language || "es-AR");
    window.addEventListener("locale:change", onLocale);
    window.addEventListener("content:refresh", onRefresh);
    return () => {
      window.removeEventListener("locale:change", onLocale);
      window.removeEventListener("content:refresh", onRefresh);
    };
  }, [fetchContent, i18n.language]);

  return <ContentContext.Provider value={state}>{children}</ContentContext.Provider>;
}

export function useContent() {
  return useContext(ContentContext);
}

/** Hook for a single content slug. Returns the string for the active locale,
 *  or a sensible fallback while loading. */
export function useContentValue(slug, fallback = "") {
  const { items, loading } = useContext(ContentContext);
  if (loading && !items[slug]) return fallback;
  return items[slug] ?? fallback;
}
